import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MenuItem, MENU_CONFIG, getEnabledMenus, getMenuById, toggleMenuEnabled, updateMenuOrder, getRealRoute } from '../config/menu-config';

@Injectable({
  providedIn: 'root'
})
export class MenuConfigService {
  private menuConfigSubject = new BehaviorSubject(MENU_CONFIG);
  public menuConfig$ = this.menuConfigSubject.asObservable();

  constructor() {}

  /**
   * Obtiene los menús habilitados por tipo
   */
  getEnabledMenus(menuType: 'primary' | 'footer' | 'mobile' = 'primary'): MenuItem[] {
    return getEnabledMenus(menuType);
  }

  /**
   * Obtiene un menú específico por ID
   */
  getMenuById(id: string, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): MenuItem | undefined {
    return getMenuById(id, menuType);
  }

  /**
   * Habilita o deshabilita un menú
   */
  toggleMenuEnabled(id: string, enabled: boolean, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): void {
    toggleMenuEnabled(id, enabled, menuType);
    this.notifyConfigChange();
  }

  /**
   * Actualiza el orden de un menú
   */
  updateMenuOrder(id: string, newOrder: number, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): void {
    updateMenuOrder(id, newOrder, menuType);
    this.notifyConfigChange();
  }

  /**
   * Obtiene la ruta real del sistema basada en la configuración
   */
  getRealRoute(configRoute: string): string {
    return getRealRoute(configRoute);
  }

  /**
   * Obtiene todos los menús primarios para navegación
   */
  getPrimaryMenus(): MenuItem[] {
    return this.getEnabledMenus('primary');
  }

  /**
   * Obtiene los menús del footer
   */
  getFooterMenus(): MenuItem[] {
    return this.getEnabledMenus('footer');
  }

  /**
   * Obtiene los menús específicos para móvil
   */
  getMobileMenus(): MenuItem[] {
    return this.getEnabledMenus('mobile');
  }

  /**
   * Verifica si un menú está habilitado
   */
  isMenuEnabled(id: string, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): boolean {
    const menu = this.getMenuById(id, menuType);
    return menu ? menu.enabled : false;
  }

  /**
   * Obtiene el número total de menús habilitados
   */
  getEnabledMenuCount(menuType: 'primary' | 'footer' | 'mobile' = 'primary'): number {
    return this.getEnabledMenus(menuType).length;
  }

  /**
   * Actualiza múltiples menús a la vez
   */
  updateMultipleMenus(updates: Array<{id: string, enabled?: boolean, order?: number}>, menuType: 'primary' | 'footer' | 'mobile' = 'primary'): void {
    updates.forEach(update => {
      if (update.enabled !== undefined) {
        this.toggleMenuEnabled(update.id, update.enabled, menuType);
      }
      if (update.order !== undefined) {
        this.updateMenuOrder(update.id, update.order, menuType);
      }
    });
    this.notifyConfigChange();
  }

  /**
   * Restaura la configuración por defecto
   */
  resetToDefaultConfig(): void {
    // Restablecer todos los menús primarios a su estado original
    MENU_CONFIG.primaryMenus.forEach(menu => {
      menu.enabled = true;
    });

    // Restablecer menús de footer (deshabilitados por defecto)
    MENU_CONFIG.footerMenus?.forEach(menu => {
      menu.enabled = false;
    });

    // Restablecer menús móviles (deshabilitados por defecto)
    MENU_CONFIG.mobileMenus?.forEach(menu => {
      menu.enabled = false;
    });

    this.notifyConfigChange();
  }

  /**
   * Exporta la configuración actual
   */
  exportConfig(): string {
    return JSON.stringify(MENU_CONFIG, null, 2);
  }

  /**
   * Importa una configuración desde JSON
   */
  importConfig(configJson: string): boolean {
    try {
      const newConfig = JSON.parse(configJson);
      // Validar estructura básica
      if (newConfig.primaryMenus && Array.isArray(newConfig.primaryMenus)) {
        Object.assign(MENU_CONFIG, newConfig);
        this.notifyConfigChange();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error importando configuración:', error);
      return false;
    }
  }

  /**
   * Notifica cambios en la configuración
   */
  private notifyConfigChange(): void {
    this.menuConfigSubject.next({ ...MENU_CONFIG });
  }

  /**
   * Observable para escuchar cambios en la configuración
   */
  onConfigChange(): Observable<typeof MENU_CONFIG> {
    return this.menuConfig$;
  }
}